import { fetchAuthSession } from "@aws-amplify/auth";

const TOKEN_EXPIRATION_KEY = "jwt_exp";
const TOKEN_KEY = "jwt_accessToken";
const TOKEN_EXPIRATION_PADDING = 1000 * 60 * 10; // 5 minutes

// Function to decode Base64 URL strings
function base64UrlDecode(base64Url: string) {
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/"); // Convert from base64url to base64 by replacing characters
  const jsonPayload = decodeURIComponent(atob(base64)); // Decode Base64 string to UTF-8

  return JSON.parse(jsonPayload);
}

function getTokenExpiration(token: string) {
  const tokenParts = token.split("."); // Split the token to get the payload (second part)
  const payload = base64UrlDecode(tokenParts[1]);

  return payload.exp * 1000;
}

class TokenManager {
  isSyncing: boolean;

  autoUpdateInterval: number;

  timeoutId: ReturnType<typeof setInterval> | null;

  constructor() {
    this.autoUpdateInterval = window.appConfig.jobs.tokenSyncInterval;
    this.timeoutId = null;
    this.isSyncing = false;

    this.setupVisibilityListener();
  }

  getAccessToken() {
    return localStorage.getItem(TOKEN_KEY);
  }

  setAccessToken(token: string) {
    localStorage.setItem(TOKEN_KEY, token);

    const expirationTime = getTokenExpiration(token);
    localStorage.setItem(TOKEN_EXPIRATION_KEY, expirationTime.toString());
  }

  async fetchToken(forceRefresh?: boolean): Promise<string | undefined> {
    try {
      this.isSyncing = true;

      const session = await fetchAuthSession({ forceRefresh });
      const idToken = session.tokens?.idToken!.toString();

      return idToken;
    } catch (error) {
      console.error("Error fetching new token:", error);
      return undefined;
    } finally {
      this.isSyncing = false;
    }
  }

  async fetchAccessToken(forceRefresh?: boolean) {
    const token = await this.fetchToken(forceRefresh);

    if (token) {
      this.setAccessToken(token);
      await this.resetAutoRefresh();
    }

    return token;
  }

  async autoRefreshToken() {
    const tokenExpiration = parseInt(localStorage.getItem(TOKEN_EXPIRATION_KEY) ?? Date.now().toString(), 10);
    const timeLeft = tokenExpiration - Date.now() - TOKEN_EXPIRATION_PADDING; // Subtract padding to ensure token is refreshed before expiration

    this.timeoutId = setTimeout(async () => {
      await this.fetchAccessToken(true);
    }, timeLeft);
  }

  async resetAutoRefresh() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    await this.autoRefreshToken();
  }

  setupVisibilityListener() {
    // BUG: 'visibilitychange' event doesn't fire when the devtools are opened
    document.addEventListener("visibilitychange", () => {
      const token = this.getAccessToken();

      if (document.visibilityState === "visible" && token) {
        this.resetAutoRefresh();
      }
    });
  }
}

export const tokenManager = new TokenManager();
